import { SQLFilters } from '../../../../api/types';
import { RegularMetricId } from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2ComponentConfig,
  KpiV2Type,
  LanguageString,
  TimePeriodBarChartComponentConfig,
  TimePeriodBarChartQueryConfig,
} from '../../../../common/components/dashboards/types';
import { Languages } from '../../../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  TimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import { StoryboardDateUtils } from '../../utils';

const OPERATIONAL_JOB_GRADE_FILTER = `EMPLOYEE.JOB_GRADE_LEVEL_1 = 'OPERATIONAL'`;
const NOT_OPERATIONAL_JOB_GRADE_FILTER = `EMPLOYEE.JOB_GRADE_LEVEL_1 != 'OPERATIONAL'`;
const NON_NULL_FUNCTION_FILTER = `EMPLOYEE.FUNCTION_LEVEL_1 IS NOT NULL`;

const YOC_AGENCY_STAFF_MP_FILTER = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_YOC' AND 
(EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'AS/RS' 
OR EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'MPS-YH') AND ${NON_NULL_FUNCTION_FILTER}`;
const JPHIL_CONTRACT_STAFF_FILTER = `EMPLOYEE.ORGANIZATION_LEVEL_1 = 'JGBL_JPHIL' 
AND EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_1 = 'Contract Employee' 
AND EMPLOYEE.EMPLOYMENT_TYPE_LEVEL_2 = 'JPHIL' AND ${NON_NULL_FUNCTION_FILTER}`;

const getHeadcountsByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  groupBy: DataFieldWithDataType,
  segmentBy: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const query: TimePeriodBarChartQueryConfig = {
    metrics: [
      {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0001_1IndividualsCount,
      },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: {
        start: nowEndOfMonth,
        end: nowEndOfMonth,
      },
    },
    segmentations: [
      {
        type: 'non-hierarchical',
        dataField: groupBy,
      },
      {
        type: 'non-hierarchical',
        dataField: segmentBy,
      },
    ],
    filters: filters ?? undefined,
  };
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(groupBy)}-${getKeyFromDataFieldWithDataType(segmentBy)}`,
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title,
    queries: [query],
    ...gridConfig,
  };
};

export const yocAgencyStaffBarChartConfig = (
  storyboardDateUtils: StoryboardDateUtils
): TimePeriodBarChartComponentConfig => {
  return getHeadcountsByDimensionChartConfig(
    storyboardDateUtils,
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
    },
    {
      [Languages.EN]:
        'YOC Agency Staff MP Capacity Chart(Excluding Operational Staff)',
      [Languages.JA]:
        'YOC Agency Staff MP Capacity Chart(Excluding Operational Staff)',
    },
    `${YOC_AGENCY_STAFF_MP_FILTER} AND ${NOT_OPERATIONAL_JOB_GRADE_FILTER}`,
    {
      gridColStart: 1,
      gridColEnd: 4,
      gridRowStart: 1,
      gridRowEnd: 9,
    }
  );
};

export const jphilContractStaffBarChartConfig = (
  storyboardDateUtils: StoryboardDateUtils
): TimePeriodBarChartComponentConfig => {
  return getHeadcountsByDimensionChartConfig(
    storyboardDateUtils,
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
    },
    {
      dataType: DataTypes.EMPLOYEE,
      dataField: EmployeeDataFields.CUSTOM_FIELD_3_LEVEL_1,
    },
    {
      [Languages.EN]:
        'JPHIL Agency Staff (Contract Staff) MP Capacity Chart(Excluding PJ Staff assigned at local sites)',
      [Languages.JA]:
        'JPHIL Agency Staff (Contract Staff) MP Capacity Chart(Excluding PJ Staff assigned at local sites)',
    },
    JPHIL_CONTRACT_STAFF_FILTER,
    {
      gridColStart: 1,
      gridColEnd: 4,
      gridRowStart: 9,
      gridRowEnd: 17,
    }
  );
};

export const getNikkiTotalNumberVsOperationalStaffFactbox = (
  storyboardDateUtils: StoryboardDateUtils
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'total-number-vs-operational-staff-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    options: {
      kpiType: KpiV2Type.DOUBLE_METRIC,
      sections: {
        leftSection: {
          key: 'totalNumber',
          title: {
            [Languages.EN]: 'Total Number',
            [Languages.JA]: 'Total Number',
          },
          query: {
            metrics: [
              {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
            ],
            timeSelection,
            filters: `${YOC_AGENCY_STAFF_MP_FILTER} AND ${NOT_OPERATIONAL_JOB_GRADE_FILTER}`,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
        rightSection: {
          key: 'operationalStaff',
          title: {
            [Languages.EN]: 'Operational Staff',
            [Languages.JA]: 'Operational Staff',
          },
          query: {
            metrics: [
              {
                type: 'RegularMetricId',
                value: RegularMetricId.Pm0001_1IndividualsCount,
              },
            ],
            timeSelection,
            filters: `${YOC_AGENCY_STAFF_MP_FILTER} AND ${OPERATIONAL_JOB_GRADE_FILTER}`,
          },
          benchmarkConfigs: [
            {
              benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
              benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
            },
          ],
        },
      },
    },
    gridColStart: 4,
    gridColEnd: 5,
    gridRowStart: 1,
    gridRowEnd: 7,
  };
  return kpiConfig;
};

export const getNikkiTotalNumberFactbox = (
  storyboardDateUtils: StoryboardDateUtils
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const timeSelection: TimeSelection = {
    type: TimeSelectionType.CalendarYearSingleValueByMonths,
    input: {
      start: nowEndOfMonth,
      end: nowEndOfMonth,
    },
  };
  const kpiConfig: KpiV2ComponentConfig = {
    chartId: 'total-number-right-factbox',
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: 'Total Number',
      [Languages.JA]: 'Total Number',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: 'totalNumber',
        query: {
          metrics: [
            {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0001_1IndividualsCount,
            },
          ],
          timeSelection,
          filters: JPHIL_CONTRACT_STAFF_FILTER,
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    gridColStart: 4,
    gridColEnd: 5,
    gridRowStart: 9,
    gridRowEnd: 15,
  };
  return kpiConfig;
};
